import React, { useLayoutEffect, useRef } from 'react';
import { fillFor } from '../wall/wallTokens';
import { QUICK_REACTIONS, ALL_REACTIONS, EMOJI_FONT, EASE, T, RADIUS } from './messagesModel';
import { fanTray, openMore } from './messagesMotion';

const PLUS = (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="square">
    <path d="M5 12h14M12 5v14" />
  </svg>
);
const CROSS = (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="square">
    <path d="M6 18L18 6M6 6l12 12" />
  </svg>
);

/**
 * The reaction tray that rises out of a bubble (handoff §4): a capsule of six quick
 * reactions with a + that opens ALL REACTIONS, and the actions menu under the bubble.
 *
 * Mine anchors right, theirs left. The parent decides when it is open; this draws it and
 * runs its own entrance the frame it mounts. Every cell stops the click so the thread's
 * tap-away handler doesn't close it out from under the tap.
 */
export default function ReactionTray({
  mine,
  reactions = [],
  more = false,
  actions = [],
  /** Badges hang under the bubble: the menu clears them. */
  badgesHang = false,
  onPick,
  onMore,
  onLess,
  bubbleEl = null,
}) {
  const capsuleRef = useRef(null);
  const moreRef = useRef(null);
  const menuRef = useRef(null);
  const side = mine ? { right: 0, left: 'auto' } : { left: 0, right: 'auto' };
  const origin = mine ? '100% 100%' : '0% 100%';
  const stop = (e) => e.stopPropagation();

  useLayoutEffect(() => {
    fanTray({ bubble: bubbleEl, capsule: capsuleRef.current, menu: menuRef.current });
    // Mount only: the entrance belongs to the open, not to every re-render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useLayoutEffect(() => { if (more) openMore(moreRef.current); }, [more]);

  const mineSet = new Set(reactions.filter((r) => r.mine).map((r) => r.emoji));
  const cellBase = {
    width: 40, height: 40, flexShrink: 0, borderRadius: '50%', border: 0, padding: 0,
    fontSize: 22, lineHeight: '40px', textAlign: 'center', display: 'block', fontFamily: EMOJI_FONT, cursor: 'pointer',
    transition: `transform 160ms ${EASE.SMOOTH}, background-color 200ms`,
  };

  return (
    <div data-tray-root onClick={stop} onMouseDown={stop} onTouchStart={stop}>
      {!more ? (
        <div
          ref={capsuleRef}
          data-tray
          style={{ position: 'absolute', bottom: '100%', ...side, marginBottom: 10, zIndex: 30, transformOrigin: origin }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 2, padding: 4, borderRadius: RADIUS.pill, border: '2px solid transparent', background: fillFor('open') }}>
            {QUICK_REACTIONS.map((emoji) => (
              <button
                key={emoji}
                type="button"
                data-fan
                data-emoji={emoji}
                aria-label={`React ${emoji}`}
                onClick={(e) => onPick?.(emoji, e.currentTarget.getBoundingClientRect())}
                className="pr-react-cell"
                style={{ ...cellBase, background: mineSet.has(emoji) ? T.off : 'transparent' }}
              >
                {emoji}
              </button>
            ))}
            <button
              type="button"
              data-fan
              aria-label="All reactions"
              onClick={onMore}
              className="pr-react-plus"
              style={{ ...cellBase, fontSize: 0, background: T.off, color: T.ink, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            >
              {PLUS}
            </button>
          </div>
        </div>
      ) : (
        <div
          ref={moreRef}
          data-more
          style={{
            position: 'absolute', bottom: '100%', ...side, marginBottom: 10, zIndex: 31, width: 312, maxWidth: 'calc(100vw - 32px)',
            borderRadius: RADIUS.allReactions, border: '2px solid transparent', background: fillFor('open'),
            padding: '12px 10px 10px', boxSizing: 'border-box', transformOrigin: origin,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 6px 8px' }}>
            <span className="font-mono font-bold" style={{ fontSize: 10, letterSpacing: '.1em', color: T.grey500 }}>ALL REACTIONS</span>
            <button
              type="button"
              onClick={onLess}
              aria-label="Back"
              style={{ width: 28, height: 28, borderRadius: '50%', border: 0, background: T.off, color: T.ink, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', padding: 0 }}
            >
              {CROSS}
            </button>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(8, 1fr)', gap: 2, maxHeight: 196, overflowY: 'auto' }}>
            {ALL_REACTIONS.map((emoji) => (
              <button
                key={emoji}
                type="button"
                data-grid
                data-emoji={emoji}
                aria-label={`React ${emoji}`}
                onClick={(e) => onPick?.(emoji, e.currentTarget.getBoundingClientRect())}
                className="pr-react-grid"
                style={{ height: 36, borderRadius: '50%', border: 0, padding: 0, background: mineSet.has(emoji) ? T.off : 'transparent', fontSize: 20, lineHeight: '36px', textAlign: 'center', display: 'block', fontFamily: EMOJI_FONT, cursor: 'pointer', transition: `transform 140ms ${EASE.SMOOTH}` }}
              >
                {emoji}
              </button>
            ))}
          </div>
        </div>
      )}

      {actions.length > 0 && (
        <div
          ref={menuRef}
          data-menu
          style={{
            position: 'absolute', top: '100%', ...side, marginTop: badgesHang ? 26 : 8, zIndex: 30, minWidth: 172,
            borderRadius: RADIUS.row, overflow: 'hidden', border: '2px solid transparent', background: fillFor('open'),
          }}
        >
          {actions.map((a, i) => (
            <button
              key={a.label}
              type="button"
              onClick={a.act}
              className="font-mono font-bold hover:opacity-60"
              style={{
                display: 'flex', width: '100%', alignItems: 'center', padding: '12px 16px', border: 0,
                borderTop: i > 0 ? `2px solid ${T.off}` : 0, background: 'transparent',
                color: a.danger ? T.danger : T.ink, fontSize: 11, letterSpacing: '.1em', cursor: 'pointer', textAlign: 'left', transition: 'opacity 200ms',
              }}
            >
              {a.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
